import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Shield, AlertTriangle, CheckCircle, XCircle, Eye, Settings, TrendingUp } from 'lucide-react';

const securityMetrics = [
  { name: 'Threat Detection Rate', value: 98.5, status: 'good', trend: 'up' },
  { name: 'False Positive Rate', value: 2.1, status: 'good', trend: 'down' },
  { name: 'Response Time (avg)', value: 45, status: 'warning', trend: 'stable', unit: 'seconds' },
  { name: 'Active Threats', value: 3, status: 'warning', trend: 'up' },
];

const securityAlerts = [
  {
    id: '1',
    severity: 'high',
    title: 'Multiple Failed Login Attempts',
    description: 'User account: admin@acme.com has 5 failed login attempts in the last 10 minutes',
    timestamp: '2 minutes ago',
    status: 'active'
  },
  {
    id: '2',
    severity: 'medium',
    title: 'Unusual API Access Pattern',
    description: 'API key usage spike detected from IP 203.0.113.45',
    timestamp: '15 minutes ago',
    status: 'investigating'
  },
  {
    id: '3',
    severity: 'low',
    title: 'Password Policy Violation',
    description: 'User attempted to set weak password',
    timestamp: '1 hour ago',
    status: 'resolved'
  },
  {
    id: '4',
    severity: 'critical',
    title: 'Potential Data Exfiltration',
    description: 'Large data download detected outside business hours',
    timestamp: '3 minutes ago',
    status: 'active'
  }
];

const securityControls = [
  { name: 'Multi-Factor Authentication', status: 'enabled', coverage: 95 },
  { name: 'Encryption at Rest', status: 'enabled', coverage: 100 },
  { name: 'Encryption in Transit', status: 'enabled', coverage: 100 },
  { name: 'Access Logging', status: 'enabled', coverage: 98 },
  { name: 'Intrusion Detection', status: 'enabled', coverage: 85 },
  { name: 'Data Loss Prevention', status: 'partially', coverage: 70 },
];

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'bg-red-500 text-white';
    case 'high':
      return 'bg-red-100 text-red-800';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'low':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'bg-red-100 text-red-800';
    case 'investigating':
      return 'bg-yellow-100 text-yellow-800';
    case 'resolved':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'enabled':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'partially':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'disabled':
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return <Shield className="h-4 w-4 text-gray-500" />;
  }
};

export default function SecurityMonitoring() {
  const activeThreats = securityAlerts.filter(alert => alert.status === 'active').length;
  const criticalAlerts = securityAlerts.filter(alert => alert.severity === 'critical').length;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Security Monitoring</h1>
          <p className="text-muted-foreground">
            Real-time security monitoring and threat detection
          </p>
        </div>
      </div>

      {/* Security Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className={criticalAlerts > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Threats</CardTitle>
            <AlertTriangle className={`h-4 w-4 ${criticalAlerts > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeThreats}</div>
            <p className="text-xs text-muted-foreground">
              {criticalAlerts} critical
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Security Score</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">87%</div>
            <p className="text-xs text-muted-foreground">
              +2% from last week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Blocked Attempts</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">247</div>
            <p className="text-xs text-muted-foreground">
              Last 24 hours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uptime</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">99.9%</div>
            <p className="text-xs text-muted-foreground">
              30-day average
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Security Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Security Alerts
          </CardTitle>
          <CardDescription>
            Recent security events requiring attention
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {securityAlerts.map((alert) => (
              <div key={alert.id} className="border rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Badge className={getSeverityColor(alert.severity)}>
                      {alert.severity.toUpperCase()}
                    </Badge>
                    <Badge className={getStatusColor(alert.status)}>
                      {alert.status}
                    </Badge>
                    <h3 className="font-medium">{alert.title}</h3>
                  </div>
                  <span className="text-sm text-muted-foreground">{alert.timestamp}</span>
                </div>
                <p className="text-sm text-muted-foreground pl-4">{alert.description}</p>
                <div className="flex justify-end space-x-2 pt-2">
                  <Button variant="outline" size="sm">
                    <Eye className="h-4 w-4 mr-2" />
                    Investigate
                  </Button>
                  {alert.status === 'active' && (
                    <Button size="sm">
                      Resolve
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Security Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Security Controls
          </CardTitle>
          <CardDescription>
            Status and coverage of security controls
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {securityControls.map((control, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(control.status)}
                  <div>
                    <h3 className="font-medium">{control.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Coverage: {control.coverage}%
                    </p>
                  </div>
                </div>
                <div className="text-right space-y-2">
                  <Badge className={control.status === 'enabled' ? 'bg-green-100 text-green-800' : control.status === 'partially' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
                    {control.status}
                  </Badge>
                  <div className="w-24">
                    <Progress value={control.coverage} className="h-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Security Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Security Metrics</CardTitle>
          <CardDescription>
            Key performance indicators for security operations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {securityMetrics.map((metric, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{metric.name}</span>
                  <Badge className={metric.status === 'good' ? 'bg-green-100 text-green-800' : metric.status === 'warning' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
                    {metric.status}
                  </Badge>
                </div>
                <div className="text-2xl font-bold">
                  {metric.value}{metric.unit ? ` ${metric.unit}` : '%'}
                </div>
                <div className="flex items-center text-sm text-muted-foreground">
                  <TrendingUp className={`h-4 w-4 mr-1 ${metric.trend === 'up' ? 'text-green-500' : metric.trend === 'down' ? 'text-red-500' : 'text-gray-500'}`} />
                  {metric.trend} trend
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}